import { motion } from "framer-motion";
import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { caseStudies, caseStudyBySlug } from "@/data/caseStudies";
import VideoModal from "@/components/VideoModal";

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const [videoOpen, setVideoOpen] = useState(false);
  const c = caseStudyBySlug(slug);
  if (!c) return <Navigate to="/case-studies" replace />;

  const tk = (key, fallback) => t(`case_studies.items.${slug}.${key}`, fallback);
  const headline = tk("headline", c.headline);
  const intro = tk("intro", c.intro);
  const partner = tk("partner", c.partner);
  const framework = tk("framework", c.framework);
  const status = tk("status", c.status);
  const phase = tk("deploymentPhase", c.deploymentPhase);
  const quote = tk("quote", c.quote);
  const tag = t(`case_studies.tags.${c.tag}`, c.tag);
  const coverage = t(`case_studies.items.${slug}.coverage`, { returnObjects: true, defaultValue: c.coverage }) || c.coverage;
  const outcomes = t(`case_studies.items.${slug}.outcomes`, { returnObjects: true, defaultValue: c.outcomes }) || c.outcomes;
  const milestoneStates = t("case_studies.detail.milestone_states", { returnObjects: true }) || {};
  const presentationTitle = tk("presentationTitle", c.presentation?.title);
  const videoTitle = tk("videoTitle", c.video?.title);

  // Build localized milestones merged with original date/state
  const localizedMilestones = (() => {
    if (!c.statusTimeline) return null;
    const tr = t(`case_studies.items.${slug}.milestones`, { returnObjects: true });
    const arr = Array.isArray(tr) ? tr : [];
    return c.statusTimeline.map((m, i) => ({
      date: m.date,
      state: m.state,
      milestone: arr[i]?.milestone || m.milestone,
      note: arr[i]?.note ?? m.note,
    }));
  })();

  // Build localized journey
  const localizedJourney = (() => {
    if (!c.partnershipJourney) return null;
    const tr = t(`case_studies.items.${slug}.journey`, { returnObjects: true });
    if (!tr || typeof tr !== "object") return c.partnershipJourney;
    const steps = (tr.steps || []).map((s, i) => ({
      num: c.partnershipJourney.steps[i]?.num || String(i + 1).padStart(2, "0"),
      title: s.title,
      desc: s.desc,
    }));
    return {
      title: tr.title || c.partnershipJourney.title,
      subtitle: tr.subtitle || c.partnershipJourney.subtitle,
      intro: tr.intro || c.partnershipJourney.intro,
      steps,
    };
  })();

  const localizedTestimonial = c.testimonial
    ? { ...c.testimonial, text: tk("testimonialText", c.testimonial.text) }
    : null;

  return (
    <article>
      <section className="relative pt-32 pb-12 overflow-hidden bg-primary-container text-white">
        <div className="absolute inset-0 tech-grid-dark opacity-40" />
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-brand/15 blur-3xl" />

        <div className="max-w-[1280px] mx-auto px-6 relative z-10">
          <Link to="/case-studies" data-testid="case-detail-back" className="inline-flex items-center gap-1 text-zinc-400 hover:text-brand text-sm mb-8 transition-colors">
            <span className="material-symbols-outlined text-[18px]">arrow_back</span>
            {t("case_studies.detail.back")}
          </Link>

          <div className="grid lg:grid-cols-3 gap-md items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-2">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="px-3 py-1 bg-brand text-white rounded-full text-[10px] font-bold uppercase tracking-widest">{c.code}</span>
                <span className="px-3 py-1 bg-zinc-900 border border-zinc-800 text-zinc-300 rounded-full text-[10px] font-bold uppercase tracking-widest">{tag}</span>
                <span className="text-xs text-zinc-400 flex items-center gap-1">
                  <span className="material-symbols-outlined text-[14px]">location_on</span>
                  {c.country}
                </span>
              </div>
              <h1 className="font-h1 text-[36px] md:text-h1 mb-4">{headline}</h1>
              <p className="text-zinc-300 text-lg max-w-3xl">{intro}</p>
              <p className="mt-6 italic text-brand font-manrope">{quote}</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="bg-white rounded-2xl p-md flex items-center justify-center aspect-square">
              <img src={c.logo} alt={`${c.code} logo`} className="max-h-full max-w-full object-contain" />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="max-w-[1280px] mx-auto px-6 py-xl">
        <div className="grid lg:grid-cols-3 gap-lg">
          <div className="lg:col-span-2 space-y-md">
            <Block title={t("case_studies.detail.regulatory_partner")} icon="account_balance">
              <p className="text-on-surface-variant">{partner}</p>
            </Block>
            <Block title={t("case_studies.detail.framework")} icon="gavel">
              <p className="text-on-surface-variant">{framework}</p>
            </Block>

            {localizedMilestones && (
              <Block title={t("case_studies.detail.milestones")} icon="timeline">
                <div className="relative pl-6 space-y-5">
                  <div className="absolute left-[7px] top-1 bottom-1 w-px bg-outline-variant" />
                  {localizedMilestones.map((m, i) => {
                    const dot = m.state === "done" ? "bg-brand" : m.state === "active" ? "bg-brand animate-pulse ring-4 ring-brand/20" : "bg-zinc-300";
                    return (
                      <motion.div key={i} initial={{ opacity: 0, x: -8 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.4, delay: i * 0.08 }} className="relative">
                        <span className={`absolute -left-[22px] top-1.5 w-3.5 h-3.5 rounded-full ${dot}`} />
                        <div className="flex flex-wrap items-baseline gap-2">
                          <span className="font-mono text-xs uppercase tracking-widest text-brand font-bold">{m.date}</span>
                          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${
                            m.state === "done" ? "bg-brand/15 text-brand"
                            : m.state === "active" ? "bg-yellow-500/15 text-yellow-700"
                            : "bg-zinc-200 text-zinc-600"
                          }`}>
                            {milestoneStates[m.state] || m.state}
                          </span>
                        </div>
                        <h4 className="font-manrope font-bold text-primary mt-1">{m.milestone}</h4>
                        {m.note && <p className="text-sm text-on-surface-variant mt-0.5">{m.note}</p>}
                      </motion.div>
                    );
                  })}
                </div>
              </Block>
            )}

            {localizedJourney && (
              <Block title={localizedJourney.title} icon="handshake">
                <div data-testid="partnership-journey">
                  <p className="font-manrope text-sm text-brand mb-2 font-bold uppercase tracking-widest">
                    {localizedJourney.subtitle}
                  </p>
                  <p className="text-on-surface-variant mb-6">{localizedJourney.intro}</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {localizedJourney.steps.map((s, i) => (
                      <motion.div
                        key={s.num}
                        initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.45, delay: i * 0.06 }}
                        className="relative bg-surface-container-low border border-outline-variant rounded-xl p-4 hover:border-brand/60 transition-colors"
                      >
                        <div className="absolute -top-2 -left-2 w-8 h-8 rounded-lg bg-brand text-white flex items-center justify-center font-manrope font-extrabold text-xs shadow-md">
                          {s.num}
                        </div>
                        <h5 className="font-manrope font-bold text-primary mb-1 mt-1">{s.title}</h5>
                        <p className="text-sm text-on-surface-variant leading-relaxed">{s.desc}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </Block>
            )}

            {localizedTestimonial && (
              <motion.blockquote
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.5 }}
                data-testid="case-testimonial"
                className="relative bg-primary-container text-white rounded-2xl p-md overflow-hidden"
              >
                <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-brand/20 blur-3xl" />
                <span className="material-symbols-outlined text-brand text-4xl mb-2">format_quote</span>
                <p className="font-manrope text-lg md:text-xl leading-snug relative z-10">{localizedTestimonial.text}</p>
                <footer className="mt-4 text-sm text-brand font-bold">
                  — {localizedTestimonial.author}
                  <span className="text-zinc-400 font-normal ml-1">· {localizedTestimonial.role}</span>
                </footer>
              </motion.blockquote>
            )}

            <Block title={t("case_studies.detail.coverage")} icon="sensors">
              <ul className="space-y-2">
                {coverage.map((x, i) => (
                  <li key={i} className="flex items-start gap-2 text-on-surface-variant">
                    <span className="material-symbols-outlined icon-fill text-brand text-[18px] mt-0.5">check_circle</span>
                    {x}
                  </li>
                ))}
              </ul>
            </Block>
            <Block title={t("case_studies.detail.outcomes")} icon="trending_up">
              <ul className="space-y-2">
                {outcomes.map((x, i) => (
                  <li key={i} className="flex items-start gap-2 text-on-surface-variant">
                    <span className="material-symbols-outlined icon-fill text-brand text-[18px] mt-0.5">check_circle</span>
                    {x}
                  </li>
                ))}
              </ul>
            </Block>
          </div>

          <aside className="space-y-md">
            <div className="bg-white rounded-2xl border border-outline-variant p-md">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">{t("case_studies.detail.status")}</div>
              <div className="flex items-center gap-2 text-primary font-manrope font-bold">
                <span className="w-2 h-2 rounded-full bg-brand animate-pulse" />
                {status}
              </div>
              {phase && (
                <div className="mt-3 pt-3 border-t border-outline-variant/50">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{t("case_studies.detail.phase")}</div>
                  <div className="text-sm font-bold text-brand">{phase}</div>
                </div>
              )}
            </div>

            {c.video && (
              <button
                type="button" onClick={() => setVideoOpen(true)} data-testid={`case-video-${c.slug}`}
                className="block w-full text-left bg-primary-container text-white rounded-2xl p-md hover:opacity-95 hover:-translate-y-0.5 transition-all relative overflow-hidden group"
              >
                <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-brand/25 blur-3xl" />
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-11 h-11 rounded-xl bg-brand flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <span className="material-symbols-outlined icon-fill text-white">play_arrow</span>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-brand font-bold">Video</span>
                  </div>
                  <div className="text-xs uppercase tracking-widest text-zinc-400 mb-1">{t("case_studies.detail.video_eyebrow")}</div>
                  <h4 className="font-manrope font-bold text-white leading-tight mb-3">{videoTitle}</h4>
                  <span className="inline-flex items-center gap-1 text-brand font-bold text-sm">
                    {t("case_studies.detail.play")}
                    <span className="material-symbols-outlined text-[16px] group-hover:translate-x-1 transition-transform">arrow_forward</span>
                  </span>
                </div>
              </button>
            )}

            {c.presentation && (
              <a
                href={`/docs/${c.presentation.file}`} download={c.presentation.file} data-testid={`case-presentation-${c.slug}`}
                className="block bg-white rounded-2xl border border-outline-variant p-md hover:border-brand hover:shadow-lg transition-all group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-11 h-11 rounded-xl bg-tertiary-fixed flex items-center justify-center">
                    <span className="material-symbols-outlined text-brand">slideshow</span>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">PDF</span>
                </div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">{t("case_studies.detail.deck_eyebrow")}</div>
                <h4 className="font-manrope font-bold text-primary leading-tight mb-3">{presentationTitle}</h4>
                <div className="flex items-center justify-between text-xs text-on-surface-variant">
                  <span>{c.presentation.size}</span>
                  <span className="flex items-center gap-1 text-brand font-bold">
                    {t("case_studies.detail.download")}
                    <span className="material-symbols-outlined text-[16px] group-hover:translate-x-1 transition-transform">download</span>
                  </span>
                </div>
              </a>
            )}

            <div className="bg-primary-container text-white rounded-2xl p-md relative overflow-hidden">
              <div className="absolute -bottom-12 -right-12 w-48 h-48 rounded-full bg-brand/15 blur-3xl" />
              <div className="relative z-10">
                <h4 className="font-h3 text-h3 mb-2">{t("case_studies.detail.talk_title")}</h4>
                <p className="text-zinc-400 text-sm mb-4">{t("case_studies.detail.talk_intro")}</p>
                <Link to="/contact" className="inline-block px-5 py-2.5 bg-brand text-white rounded-lg font-bold text-sm hover:opacity-90 transition-all">
                  {t("case_studies.detail.talk_cta")}
                </Link>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-outline-variant p-md">
              <h4 className="font-h3 text-h3 mb-3">{t("case_studies.detail.other_deployments")}</h4>
              <ul className="space-y-2">
                {caseStudies.filter((v) => v.slug !== slug).map((v) => (
                  <li key={v.slug}>
                    <Link to={`/case-studies/${v.slug}`} className="flex items-center justify-between py-2 text-sm text-primary hover:text-brand transition-colors">
                      <span className="font-bold">{v.code} · {v.country}</span>
                      <span className="material-symbols-outlined text-[18px]">arrow_forward</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {c.video && (
        <VideoModal
          open={videoOpen}
          onClose={() => setVideoOpen(false)}
          src={c.video.src}
          title={videoTitle}
        />
      )}
    </article>
  );
}

function Block({ title, icon, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.5 }}
      className="bg-white rounded-2xl border border-outline-variant p-md"
    >
      <div className="flex items-center gap-3 mb-3">
        <span className="w-10 h-10 rounded-lg bg-tertiary-fixed flex items-center justify-center">
          <span className="material-symbols-outlined text-brand">{icon}</span>
        </span>
        <h3 className="font-h3 text-h3 text-primary">{title}</h3>
      </div>
      {children}
    </motion.div>
  );
}
